import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import "./TournamentAppearanceEditor.css";

const DEFAULT_PRIMARY = "#163b38";
const DEFAULT_SECONDARY = "#315e59";
const isColor = value => /^#[0-9a-f]{6}$/i.test(value);
// Minimum black scrim (rounded up to 1%) for WCAG AA white body text.
// Check both sRGB gradient endpoints; photo pixels conservatively use white.
const readableScrim = colors => {
  const channels = colors.map(color => color.slice(1).match(/../g).map(value => parseInt(value, 16) / 255));
  for (let percent = 0; percent <= 100; percent += 1) {
    const opacity = percent / 100;
    const readable = channels.every(rgb => {
      const linear = rgb.map(value => {
        const channel = value * (1 - opacity);
        return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
      });
      const luminance = 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
      return 1.05 / (luminance + 0.05) >= 4.5;
    });
    if (readable) return opacity;
  }
  return 1;
};
// Saved storage paths belong to the API host, not the frontend host.
const assetUrl = value => {
  if (typeof value !== "string" || value.startsWith("//") || /[\\\s]/.test(value)) return "";
  if (value.startsWith("/api/storage/")) {
    try { return new URL(value, new URL(API, window.location.origin)).href; } catch { return ""; }
  }
  return /^(https?:\/\/|\/(?!\/))/.test(value) ? value : "";
};

export default function TournamentAppearanceEditor({ tournament, headers, onRefresh }) {
  const [saved, setSaved] = useState(tournament);
  const [primary, setPrimary] = useState(tournament.primary_color || DEFAULT_PRIMARY);
  const [secondary, setSecondary] = useState(tournament.secondary_color || DEFAULT_SECONDARY);
  const [mode, setMode] = useState(tournament.sidebar_background_mode || "gradient");
  const [assets, setAssets] = useState({});
  const [removed, setRemoved] = useState({});
  const [previews, setPreviews] = useState({});
  const [fileErrors, setFileErrors] = useState({});
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const logoInput = useRef(null);
  const photoInput = useRef(null);

  useEffect(() => {
    const urls = Object.fromEntries(Object.entries(assets).map(([key, file]) => [key, URL.createObjectURL(file)]));
    setPreviews(urls);
    return () => Object.values(urls).forEach(url => URL.revokeObjectURL(url));
  }, [assets]);

  const clearInputs = () => {
    if (logoInput.current) logoInput.current.value = "";
    if (photoInput.current) photoInput.current.value = "";
  };
  const resetTo = value => {
    setPrimary(value.primary_color || DEFAULT_PRIMARY);
    setSecondary(value.secondary_color || DEFAULT_SECONDARY);
    setMode(value.sidebar_background_mode || "gradient");
    setAssets({});
    setRemoved({});
    setFileErrors({});
    setError("");
    clearInputs();
  };
  const edited = () => { setMessage(""); setError(""); };
  const choose = (key, input) => {
    const file = input.files[0];
    if (!file) return;
    edited();
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type) || file.size > 10 * 1024 * 1024) {
      setFileErrors(current => ({ ...current, [key]: "Choose a PNG, JPEG, or WebP up to 10 MB." }));
      input.value = "";
      return;
    }
    setFileErrors(current => ({ ...current, [key]: "" }));
    setAssets(current => ({ ...current, [key]: file }));
    setRemoved(current => ({ ...current, [key]: false }));
  };
  const remove = key => {
    edited();
    setAssets(current => { const next = { ...current }; delete next[key]; return next; });
    setRemoved(current => ({ ...current, [key]: true }));
    setFileErrors(current => ({ ...current, [key]: "" }));
    const input = key === "logo" ? logoInput.current : photoInput.current;
    if (input) input.value = "";
  };
  const save = async event => {
    event.preventDefault();
    if (busy.current || Object.values(fileErrors).some(Boolean)) return;
    if (![primary, secondary].every(isColor)) {
      setError("Use a six-digit hex color, for example #163b38.");
      return;
    }
    setError("");
    setMessage("");
    const body = new FormData();
    body.append("primary_color", primary);
    body.append("secondary_color", secondary);
    body.append("sidebar_background_mode", mode);
    body.append("remove_logo", String(Boolean(removed.logo)));
    body.append("remove_sidebar_image", String(Boolean(removed.sidebar_image)));
    Object.entries(assets).forEach(([key, file]) => body.append(key, file));
    busy.current = true;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/modules/tournaments/${tournament.tournament_id}/appearance`, body, { headers });
      setSaved(data.tournament);
      resetTo(data.tournament);
      setMessage("Appearance saved.");
      // A refresh failure must never misrepresent a completed server write.
      try { await onRefresh?.(data.tournament); }
      catch { setMessage("Appearance saved. Workspace refresh failed; reload the workspace to update other details."); }
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      setError(`Appearance could not be saved. ${typeof detail === "string" ? detail : "Please try again."}`);
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };

  const logo = removed.logo ? "" : previews.logo || assetUrl(saved.logo_url);
  const photo = removed.sidebar_image ? "" : previews.sidebar_image || assetUrl(saved.sidebar_image_url);
  const first = isColor(primary) ? primary : DEFAULT_PRIMARY;
  const second = isColor(secondary) ? secondary : DEFAULT_SECONDARY;
  const opacity = readableScrim(mode === "photo" && photo ? ["#ffffff"] : mode === "gradient" ? [first, second] : [first]);
  const shade = `linear-gradient(rgba(0,0,0,${opacity}),rgba(0,0,0,${opacity}))`;
  const backgroundImage = mode === "photo" && photo
    ? `${shade}, url(${JSON.stringify(photo)})`
    : mode === "gradient" ? `${shade}, linear-gradient(145deg, ${first}, ${second})` : shade;

  return <section className="tournament-appearance" aria-label="Tournament appearance">
    <aside className="ta-preview" aria-label="Live appearance preview" style={{ backgroundColor: first, backgroundImage }}>
      <div className="ta-preview-label">Live preview · not saved until you save</div>
      {logo && <img className="ta-logo" src={logo} alt="Tournament logo" />}
      <h3>{tournament.name}</h3>
      {tournament.description && <p>{tournament.description}</p>}
      <details className="ta-details" open><summary>Tournament details</summary><dl>
        <dt>Dates</dt><dd>{tournament.starts_on || "To be confirmed"}{tournament.ends_on ? ` – ${tournament.ends_on}` : ""}</dd>
        <dt>Location</dt><dd>{tournament.location || "To be confirmed"}</dd>
        <dt>Application deadline</dt><dd>{tournament.registration_closes_on || "To be confirmed"}</dd>
      </dl></details>
      <footer><span>Powered by</span><img src="/brand/sporthouse-public-light.png" alt="Sporthouse" /></footer>
    </aside>
    <div className="ta-content">
      <h3>Make it your tournament.</h3>
      <p>Give the registration page its own identity. Preview changes here, then save to update the public page.</p>
      <form onSubmit={save} noValidate aria-busy={saving}>
        <fieldset disabled={saving}>
          <legend className="ta-sr-only">Appearance settings</legend>
          <section className="ta-section">
            <h4>Tournament logo</h4>
            <p>Transparent PNG works best. Your artwork stays uncropped.</p>
            <label htmlFor="ta-logo">Choose a logo</label>
            <input id="ta-logo" ref={logoInput} type="file" accept="image/png,image/jpeg,image/webp" aria-describedby="ta-file-help ta-logo-error" aria-invalid={Boolean(fileErrors.logo)} onChange={e => choose("logo", e.target)} />
            {fileErrors.logo && <p id="ta-logo-error" role="alert">{fileErrors.logo}</p>}
            <button type="button" onClick={() => remove("logo")}>Remove logo</button>
          </section>
          <section className="ta-section">
            <h4>Sidebar background</h4>
            <p>Keep your identity visible while the team completes its application.</p>
            <div className="ta-modes" role="group" aria-label="Sidebar background mode">
              {["gradient", "solid", "photo"].map(value => <button type="button" key={value} aria-pressed={mode === value} onClick={() => { edited(); setMode(value); }}>{value[0].toUpperCase() + value.slice(1)}</button>)}
            </div>
            <label htmlFor="ta-photo">Sidebar photo</label>
            <input id="ta-photo" ref={photoInput} type="file" accept="image/png,image/jpeg,image/webp" aria-describedby="ta-file-help ta-photo-error" aria-invalid={Boolean(fileErrors.sidebar_image)} onChange={e => choose("sidebar_image", e.target)} />
            {fileErrors.sidebar_image && <p id="ta-photo-error" role="alert">{fileErrors.sidebar_image}</p>}
            <button type="button" onClick={() => remove("sidebar_image")}>Remove photo</button>
            <p className="ta-help">Backgrounds are darkened for readable white text.{mode === "photo" && !photo ? " Choose a photo to use photo mode; your primary color is shown until then." : ""}</p>
            <div className="ta-colors">
              {[["primary", "Primary color", primary, setPrimary, first], ["secondary", "Secondary color", secondary, setSecondary, second]].map(([key, label, value, setter, fallback]) => <fieldset key={key}>
                <legend>{label}</legend>
                <span className="ta-color-input">
                  <label className="ta-sr-only" htmlFor={`ta-${key}-picker`}>{label} picker</label>
                  <input id={`ta-${key}-picker`} type="color" value={fallback} onChange={e => { edited(); setter(e.target.value); }} />
                  <label className="ta-sr-only" htmlFor={`ta-${key}-hex`}>{label}</label>
                  <input id={`ta-${key}-hex`} value={value} spellCheck={false} maxLength={7} aria-invalid={!isColor(value)} onChange={e => { edited(); setter(e.target.value); }} />
                </span>
              </fieldset>)}
            </div>
          </section>
          <p id="ta-file-help" className="ta-help">PNG, JPEG, or WebP up to 10 MB per image. Branding images are public. Do not upload private player documents or photos you do not have rights to use.</p>
          <p className="ta-notice">Changes remain in this preview until you save. Discard changes restores the last saved appearance.</p>
          <div className="ta-actions"><button type="button" onClick={() => { resetTo(saved); setMessage("Changes discarded."); }}>Discard changes</button><button className="ta-save" type="submit" disabled={saving}>{saving ? "Saving…" : "Save appearance"}</button></div>
        </fieldset>
        {error && <p role="alert">{error}</p>}
        <p role="status" aria-live="polite">{message}</p>
      </form>
    </div>
  </section>;
}
